import { useCallback, useState, useEffect } from 'react';
import { CircularProgress, Grid, Typography } from '@mui/material';
import { useLocation, useParams } from 'react-router-dom';
import {
  createOperationIncident,
  getOperationSummary,
  getOperationActivities,
  OperationActivity,
  OperationSummary
} from '../../../networkRequest/operationsService';
import { useUser } from '@hooks/useUser';
import { PageNavigation, Toast } from '@components';
import Activity from './components/Activity';
import ActivitiesMenu from './components/ActivitiesMenu';
import CreateActivity from './components/CreateActivity';
import EditActivityDialog from './components/EditActivityDialog';
import DeleteActivityDialog from './components/DeleteActivityDialog';
import CreateActivityIncidentDialog from './components/CreateActivityIncidentDialog';
import { COPY, PAGE_PATHS } from '../../../constants';

const ERROR_MESSAGES = {
  ACTIVITY_INCIDENT_CREATE: 'Failed to mark activity as incident',
} as const;

const OperationActivityPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { user } = useUser();

  const [loading, setLoading] = useState<boolean>(false);
  const [operation, setOperation] = useState<OperationSummary | null>(null);
  const [activities, setActivities] = useState<OperationActivity[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [deleteActivityDialogOpen, setDeleteActivityDialogOpen] = useState<boolean>(false);
  const [editActivityDialogOpen, setEditActivityDialogOpen] = useState<boolean>(false);
  const [selectedActivity, setSelectedActivity] = useState<OperationActivity | null>(null);
  const [createActivityIncidentDialogOpen, setCreateActivityIncidentDialogOpen] = useState<boolean>(false);
  const [newIncidentId, setNewIncidentId] = useState<string | null>(null);

  const fetchOperation = useCallback(async () => {
    if (!id || !user?.accessToken) {
      return;
    }
    const { data, error } = await getOperationSummary(user.accessToken, id);

    if (data) {
      setOperation(data);
    } else {
      setError(error ?? 'Failed to load operation');
    }
  }, [id, user?.accessToken]);

  const fetchOperationActivities = useCallback(async () => {
    if (!id || !user?.accessToken) {
      return;
    }
    const { data, error } = await getOperationActivities({ accessToken: user.accessToken, operationId: id });
    if (data) {
      setActivities(data);
    } else {
      setError(error ?? 'Failed to load operation activities');
    }
  }, [id, user?.accessToken]);

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.accessToken || !id) return;

      setLoading(true);
      setError(null);

      try {
        await Promise.all([
          fetchOperation(),
          fetchOperationActivities()
        ]);
      } catch {
        setError('Failed to load operation activities data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, user?.accessToken, fetchOperation, fetchOperationActivities]);

  useEffect(
    () => {
      // When loading finishes, scroll to the bookmarked element
      if (location.hash.length > 0) {
        const elementId = location.hash.substring(1);
        const element = document.getElementById(elementId);
        if (element !== null) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    },
    [loading, location.hash]
  );

  const handleSaveActivity = (newActivity: OperationActivity) => {
    setActivities((prev) => ([
      newActivity,
      ...prev
    ]))
  };

  const handleDeleteActivity = async () => {
    setActivities((prev) => prev.filter((activity) => activity.id !== selectedActivity?.id));
  };

  const handleEditActivity = async () => {
    await fetchOperationActivities();
  };

  const handleCreateActivityIncident = async (activity: OperationActivity) => {
    if (!id || !user?.accessToken) {
      return;
    }
    try {
      const { data, error } = await createOperationIncident({
        accessToken: user.accessToken,
        operationId: id,
        params: {
          title: activity.description.slice(0, 50) + '...',
          incident_type: '',
          performed_at: activity.performed_at,
          official_ids: activity.officials.map((official) => official.id),
          activity_id: activity.id,
        }
      });

      if (data) {
        setNewIncidentId(data.id);
        setCreateActivityIncidentDialogOpen(true);
        fetchOperationActivities();
      }
      if (error) {
        setError(ERROR_MESSAGES.ACTIVITY_INCIDENT_CREATE);
      }
    } catch {
      setError(ERROR_MESSAGES.ACTIVITY_INCIDENT_CREATE);
    }
  };

  if (!id || !user?.accessToken) return null;

  return (
    <>
      <DeleteActivityDialog
        open={deleteActivityDialogOpen}
        onClose={setDeleteActivityDialogOpen}
        onDeleteActivity={handleDeleteActivity}
        selectedActivity={selectedActivity}
        setSelectedActivity={setSelectedActivity}
        setError={setError}
      />
      <EditActivityDialog
        open={editActivityDialogOpen}
        onClose={setEditActivityDialogOpen}
        onEditActivity={handleEditActivity}
        selectedActivity={selectedActivity}
        setSelectedActivity={setSelectedActivity}
        operationId={operation?.id ?? ''}
        setError={setError}
      />
      <CreateActivityIncidentDialog
        open={createActivityIncidentDialogOpen}
        onClose={setCreateActivityIncidentDialogOpen}
        operationId={id}
        incidentId={newIncidentId}
        setIncidentId={setNewIncidentId}
      />
      <PageNavigation
        breadcrumbs={[
          { display: COPY.titles.operations, path: PAGE_PATHS.operations },
          { display: operation?.title ?? '', path: `${PAGE_PATHS.operations}/${id}` }
        ]}
        controls={[<ActivitiesMenu operation={operation} activities={activities} />]}
      />
      <Grid
        container
        sx={{
          justifyContent: 'flex-start',
          pt: '32px'
        }}>
        <Grid size={9}>
          <Typography variant='xxl'>Activity</Typography>
        </Grid>
        <Grid size={9}>
          <CreateActivity operationId={id} onSave={handleSaveActivity} onError={setError} />
        </Grid>
        {
          loading
            ? <Grid
            size={9}
            container
            sx={{
              justifyContent: 'center',
              alignItems: 'center'
            }}><CircularProgress /></Grid>
            : (
              <Grid size={9}>
                {
                  activities?.map((activity) => {
                    return (
                      <Activity
                        key={activity.id}
                        operationId={id}
                        activity={activity}
                        onEdit={() => { setEditActivityDialogOpen(true); }}
                        onDelete={() => { setDeleteActivityDialogOpen(true); }}
                        setSelectedItem={setSelectedActivity}
                        handleCreateActivityIncident={handleCreateActivityIncident}
                      />
                    )
                  })
                }
              </Grid>
            )
        }
      </Grid>
      {error && (
        <Toast
          type='error'
          onClose={() => setError(null)}
        >
          {error}
        </Toast>
      )}
    </>
  );
}

export default OperationActivityPage;